import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, HardHat, KeyRound } from "lucide-react";
import { ProfiloFirma } from "@/components/ProfiloFirma";

const emptyForm = { name: "", email: "", password: "", role: "operatore" };

export default function Utenti() {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/users", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Utente creato");
      setOpen(false);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Utente eliminato");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const pwMut = useMutation({
    mutationFn: async ({ id, password }) => (await api.put(`/users/${id}/password`, { password })).data,
    onSuccess: () => {
      toast.success("Password reimpostata");
      setPwUser(null);
      setPwValue("");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submit = () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Compila nome, email e password");
      return;
    }
    if (form.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    createMut.mutate(form);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Utenti
          </h1>
          <p className="text-slate-500 mt-1">
            Gli operatori possono fare tutto tranne eliminare commesse e dipendenti.
          </p>
        </div>
        {isAdmin && (
        <Button onClick={() => setOpen(true)} data-testid="add-user-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo Utente
        </Button>
        )}
      </div>

      <ProfiloFirma />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        {isLoading ? (
          <div className="py-12 text-center text-slate-400">Caricamento…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Ruolo</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{u.email}{u.telefono && <span className="block text-slate-400">{u.telefono}</span>}{u.ha_firma && <span className="block text-emerald-600">firma caricata</span>}</td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                        <ShieldCheck className="h-3.5 w-3.5" /> Amministratore
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700 border border-sky-200">
                        <HardHat className="h-3.5 w-3.5" /> Operatore
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isAdmin && (<>
                    <button
                      onClick={() => { setPwUser(u); setPwValue(""); }}
                      className="text-slate-300 hover:text-primary mr-3"
                      data-testid={`reset-pw-${u.email}`}
                      title="Reimposta password"
                    >
                      <KeyRound className="h-4 w-4" />
                    </button>
                    {u.id !== user?.id && (
                      <button
                        onClick={() => delMut.mutate(u.id)}
                        className="text-slate-300 hover:text-red-600"
                        data-testid={`delete-user-${u.email}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Nuovo Utente</DialogTitle>
            <DialogDescription>Crea un account admin o operatore.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatore">Operatore (inserisce ore/costi)</SelectItem>
                  <SelectItem value="admin">Amministratore (accesso completo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              disabled={createMut.isPending}
             
              data-testid="user-save-button"
            >
              Crea Utente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Reimposta password</DialogTitle>
            <DialogDescription>Imposta una nuova password (almeno 6 caratteri).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Nuova password per{" "}
              <span className="font-medium text-slate-700">{pwUser?.email}</span>
            </p>
            <Input
              type="password"
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              placeholder="Almeno 6 caratteri"
              data-testid="reset-pw-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>
              Annulla
            </Button>
            <Button
              onClick={() =>
                pwValue.length < 6
                  ? toast.error("La password deve avere almeno 6 caratteri")
                  : pwMut.mutate({ id: pwUser.id, password: pwValue })
              }
              disabled={pwMut.isPending}
             
              data-testid="reset-pw-save"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
